import { useMemo } from "react"
import { BENCHMARK_COLORS } from "~/ui-rtk/pages/WebMerchandising/types"

export interface BenchmarkBarProps {
  /** Current CVR value from selected date range */
  currentValue: number
  /** Left edge of bar (worst observed value) */
  worstValue: number
  /** Middle reference point (average value) */
  avgValue: number
  /** Right edge of bar (best observed value) */
  bestValue: number
  /** Format function for values (default: percentage) */
  formatValue?: (v: number) => string
  /** Show Worst/Avg/Best labels below bar (default: true) */
  showLabels?: boolean
}

/**
 * Segment configuration for the benchmark bar.
 * 4 distinct color blocks representing performance zones.
 */
const SEGMENTS = [
  { color: BENCHMARK_COLORS.segmentRed, label: "Poor" },
  { color: BENCHMARK_COLORS.segmentOrange, label: "Needs Work" },
  { color: BENCHMARK_COLORS.segmentLightGreen, label: "Good" },
  { color: BENCHMARK_COLORS.segmentGreen, label: "Great" },
]

/**
 * Calculate marker position as percentage of bar width.
 * Clamped to 2%-98% to keep marker visible on bar edges.
 */
const calculateMarkerPosition = (
  value: number,
  worst: number,
  best: number,
): number => {
  if (best === worst) return 50 // Avoid division by zero
  const position = ((value - worst) / (best - worst)) * 100
  return Math.max(2, Math.min(98, position))
}

/**
 * Calculate segment widths based on benchmark values.
 * Segments are distributed relative to worst, avg, and best values.
 *
 * Handles edge cases:
 * - If best === worst: equal distribution
 * - If avg is outside [worst, best] range: clamp to valid range
 * - Ensures all widths are non-negative
 */
const calculateSegmentWidths = (
  worst: number,
  avg: number,
  best: number,
): number[] => {
  // Handle edge case: no range
  if (best <= worst) return [25, 25, 25, 25]

  // Clamp avg to valid range to handle data quality issues
  const clampedAvg = Math.max(worst, Math.min(best, avg))

  const range = best - worst
  const avgPosition = ((clampedAvg - worst) / range) * 100

  // Red segment: 0% to halfway between worst and avg
  const redWidth = Math.max(0, avgPosition / 2)
  // Orange segment: halfway to avg
  const orangeWidth = Math.max(0, avgPosition / 2)
  // Light green segment: avg to halfway between avg and best
  const lightGreenWidth = Math.max(0, (100 - avgPosition) / 2)
  // Green segment: halfway to best
  const greenWidth = Math.max(0, (100 - avgPosition) / 2)

  return [redWidth, orangeWidth, lightGreenWidth, greenWidth]
}

/**
 * BenchmarkBar Component
 *
 * Renders a segmented benchmark bar visualization with:
 * - 4 distinct color segments (red -> orange -> light green -> green)
 * - Current value marker ABOVE the bar
 * - Benchmark labels (Worst/Avg/Best) BELOW the bar
 *
 * Design inspired by Google PageSpeed Insights.
 */
export default function BenchmarkBar({
  currentValue,
  worstValue,
  avgValue,
  bestValue,
  formatValue = (v: number) => `${v.toFixed(0)}%`,
  showLabels = true,
}: BenchmarkBarProps) {
  const markerPosition = useMemo(
    () => calculateMarkerPosition(currentValue, worstValue, bestValue),
    [currentValue, worstValue, bestValue],
  )

  const segmentWidths = useMemo(
    () => calculateSegmentWidths(worstValue, avgValue, bestValue),
    [worstValue, avgValue, bestValue],
  )

  const avgMarkerPosition = useMemo(
    () => calculateMarkerPosition(avgValue, worstValue, bestValue),
    [avgValue, worstValue, bestValue],
  )

  return (
    <div className="relative w-full min-w-[200px] pt-6">
      {/* Current Value Marker (ABOVE bar) */}
      <div
        className="absolute top-0 flex flex-col items-center"
        style={{ left: `${markerPosition}%`, transform: "translateX(-50%)" }}
      >
        <span className="text-base font-semibold text-white whitespace-nowrap">
          {formatValue(currentValue)}
        </span>
        <div className="w-0.5 h-4 bg-white opacity-80" />
      </div>

      {/* Segmented Bar - taller with visible gaps between segments */}
      <div className="flex h-3 gap-1">
        {SEGMENTS.map((segment, index) => (
          <div
            key={index}
            className="h-full rounded-sm"
            style={{
              width: `${segmentWidths[index]}%`,
              backgroundColor: segment.color,
            }}
          />
        ))}
      </div>

      {/* Benchmark Labels (BELOW bar) */}
      {showLabels && (
        <div className="relative h-10 mt-2">
          {/* Worst (left-aligned) */}
          <div className="absolute left-0 flex flex-col items-start">
            <span className="text-sm text-white font-medium">
              {formatValue(worstValue)}
            </span>
            <span className="text-xs text-gray-400">Worst</span>
          </div>

          {/* Avg (positioned at avg%) */}
          <div
            className="absolute flex flex-col items-center"
            style={{
              left: `${avgMarkerPosition}%`,
              transform: "translateX(-50%)",
            }}
          >
            <span className="text-sm text-white font-medium">
              {formatValue(avgValue)}
            </span>
            <span className="text-xs text-gray-400">Avg</span>
          </div>

          {/* Best (right-aligned) */}
          <div className="absolute right-0 flex flex-col items-end">
            <span className="text-sm text-white font-medium">
              {formatValue(bestValue)}
            </span>
            <span className="text-xs text-gray-400">Best</span>
          </div>
        </div>
      )}
    </div>
  )
}
